import { CACHE_TTL_MS, MAX_CACHE_SIZE } from '../constants';

interface CacheEntry {
  value: unknown;
  timestamp: number;
}

const normalizeQueryValue = (value?: number | string | null): string =>
  value == null ? '__null__' : value === '' ? '__empty__' : String(value);

// Module-level cache (shared across all consumers, cleared on browser refresh)
const responseCache = new Map<string, CacheEntry>();

export const getCache = (key: string): unknown => {
  const entry = responseCache.get(key);
  if (!entry) return undefined;

  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    responseCache.delete(key);
    return undefined;
  }

  return entry.value;
};

export const setCache = (key: string, value: unknown) => {
  if (responseCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = responseCache.keys().next().value;
    if (oldestKey) responseCache.delete(oldestKey);
  }
  responseCache.set(key, { value, timestamp: Date.now() });
};

export const invalidateCache = (predicate: (key: string) => boolean): void => {
  for (const key of responseCache.keys()) {
    if (predicate(key)) {
      responseCache.delete(key);
    }
  }
};

export const toQueryStringValue = (
  value?: number | string | null,
): string | undefined => (value == null ? undefined : String(value));

export const buildCacheKey = (
  action: string,
  params?: Record<string, number | string | null | undefined>,
): string => {
  if (!params) {
    return action;
  }

  return `${action}|${Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${normalizeQueryValue(v)}`)
    .join('|')}`;
};

/** Clears all cached entries. Useful for testing. */
export const clearCache = () => responseCache.clear();
